import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { GeoUtils } from "./GeoUtils";
import { OpeningGeometry } from "./OpeningGeometry";
import { RoomManager } from './../../RoomManager';
import { WallOpening } from "../WallOpening";
import { BoolProperty, PropertyChangedResult } from "@egr/wcf/modules/core/prop";
import { degToRad } from '@egr/wcf/modules/utils/math';
export class Door extends OpeningGeometry {
    constructor(pRoomManager, pOpening) {
        super(pRoomManager, pOpening);
        this.mOpening = pOpening;
        this.mOpening.OpeningParams.privacyFilter = false;
        this.mOpening.OpeningParams.blindVisible = false;
        this.mOpening.OpeningParams.blindDepth = 0.07;
        this.mOpening.OpeningParams.blindOpeningAngle = 60;
        this.mOpening.OpeningParams.blindOpeningWidth = 0;
        this.mOpening.OpeningParams.ColumnCount = 2;
        this.mOpening.OpeningParams.minWidth = 0.46;
        this.mOpening.OpeningParams.blindOpeningAngle = 45;
        this.mOpening.OpeningParams.openingAngle = 0;
        this.mOpening.OpeningParams.frameOffset = 0.03;
        this.mOpening.OpeningParams.fanlight = false;
        this.mOpening.OpeningParams.fanlightHeight = 0.5;
        this.mOpening.OpeningParams.doorType = WallOpening.DoorTypes.solid;
        this.mOpening.OpeningParams.doorTypeDouble = false;
        this.mHandlePos = new Vector3(0, 0, 0);
        this.mHingePos = new Vector3(0, 0, 0);
    }
    getType() {
        return ("Door");
    }
    get canMirrorV() {
        if (this.mOpening.OpeningParams.doorTypeDouble) {
            return (false);
        }
        return (true);
    }
    get handlePosition() {
        return (this.mHandlePos);
    }
    get hingePosition() {
        return (this.mHingePos);
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = 0.89;
        if (this.mOpening.OpeningParams.ColumnCount == 2) {
            tParams.width = 1.51;
        }
        tParams.height = 2.01;
        tParams.floorOffset = 0.0;
        return (tParams);
    }
    create2dGeometry(pParent) {
        const tdoubleFak = this.mOpening.OpeningParams.doorTypeDouble ? 0.5 : 1;
        const tdoubleFakInv = this.mOpening.OpeningParams.doorTypeDouble ? 1 : 0.5;
        let tW = this.mOpening.width * tdoubleFak;
        let tHW = tW * tdoubleFakInv;
        let tHD = this.mOpening.depth * 0.5;
        let tPoints = new Array();
        //left line
        tPoints.push(new Vector3(-tHW, 0.0, 0.0 - tHD));
        tPoints.push(new Vector3(-tHW, 0.0, -tW - tHD));
        //arc
        let tCosA;
        let tSinA;
        for (let i = 0; i < 10; i++) {
            let tDeg = degToRad(i * 10.0 - 90);
            tCosA = Math.cos(tDeg);
            tSinA = Math.sin(tDeg);
            tPoints.push(new Vector3(tCosA * tW - tHW, 0.0, tSinA * tW - tHD));
        }
        if (this.mOpening.OpeningParams.doorTypeDouble) {
            //right line
            tPoints.push(new Vector3(tHW, 0.0, 0.0 - tHD));
            tPoints.push(new Vector3(tHW, 0.0, -tW - tHD));
            //arc
            let tCosAR;
            let tSinAR;
            for (let i = 0; i < 10; i++) {
                let tDeg = degToRad(i * 10.0 - 90);
                tCosAR = Math.cos(tDeg);
                tSinAR = Math.sin(tDeg);
                tPoints.push(new Vector3(-tCosAR * tW + tHW, 0.0, tSinAR * tW - tHD));
            }
        }
        if (this.mOpening.mirrorVertical) {
            for (let tPoint of tPoints) {
                tPoint.x = -tPoint.x;
            }
        }
        if (this.mOpening.mirrorHorizontal) {
            for (let tPoint of tPoints) {
                tPoint.z = -tPoint.z;
            }
        }
        let tLines = CreateLines("", {
            points: tPoints,
            updatable: false,
            instance: null
        }, this.mScene);
        tLines.isPickable = false;
        tLines.intersectionThreshold = 0;
        tLines.color = Color3.Black();
        tLines.position.y = RoomManager.WALLOPENING_2D_LINE_HEIGHT;
        tLines.parent = pParent;
        this.prepare2dMesh(tLines);
    }
    create3dGeometry(pParent) {
        this.createFrame(pParent);
        if (this.mOpening.OpeningParams.fanlight)
            this.createFanLight(pParent);
        this._createHinge(pParent);
    }
    getProperties() {
        let tProperties = new Array();
        let tYesNoChoices = new Array();
        for (let tYesNo of this.mRoomManager._YesNo)
            tYesNoChoices.push(this.mRoomManager._getYesNoPropValue(tYesNo));
        //set privacy filter
        let tPrivacyFilterProp = new BoolProperty(this.mOpening, Door.PROP_KEY_PRIVACYFILTER, this.mRoomManager.resources.propertyPrivacyFilter);
        tPrivacyFilterProp.choiceList = true;
        tPrivacyFilterProp.getChoices = () => Promise.resolve(tYesNoChoices);
        tPrivacyFilterProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mOpening.OpeningParams.privacyFilter);
        tPrivacyFilterProp.setValue = (pValue) => {
            if (pValue != null) {
                let tStr = BoolProperty.ConvertValueToBool(pValue);
                this.setPrivacyFilter(tStr, this.mOpening.OpeningParams);
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tPrivacyFilterProp.visible = false;
        switch (this.mOpening.type) {
            case "Door":
                if (this.mOpening.OpeningParams.doorType == WallOpening.DoorTypes.glass ||
                    this.mOpening.OpeningParams.doorType == WallOpening.DoorTypes.inlay) {
                    tPrivacyFilterProp.visible = true;
                }
                break;
            default:
                tPrivacyFilterProp.visible = false;
        }
        tProperties.push(tPrivacyFilterProp);
        return (tProperties);
    }
    serializeIOData() {
        return (this.mOpening.OpeningParams);
    }
    createFrame(pParent) {
        let tW = this.mOpening.width;
        let tH = this.mOpening.height;
        let tD = this.mOpening.depth;
        let tFrame = GeoUtils.createOpenFrame_CAG(tW + Door.FRAME_OFFSET * 2, tH + Door.FRAME_OFFSET, tD + Door.FRAME_DEPTH_OFFSET * 2, Door.FRAME_THICKNESS, pParent, this.mScene);
        if (tFrame != null) {
            tFrame.material = this.mOpening.getMaterialofSet(this.mOpening.MaterialSet_Frame);
            if (this.mOpening.wall != null)
                tFrame.name = "room:wall|" + this.mOpening.wall.id.toString();
            this.prepare3dMesh(tFrame);
        }
        return (tFrame);
    }
    createFanLight_glass(pParent) {
        let tW = this.mOpening.width;
        let tH = this.mOpening.height;
        let tfanLightGlass = CreateBox("room:opening:fanlightGlass", { height: this.mOpening.OpeningParams.fanlightHeight - Door.FRAME_THICKNESS,
            width: tW,
            depth: 0.005
        }, this.mScene);
        if (tfanLightGlass != null) {
            tfanLightGlass.parent = pParent;
            //tfanLightGlass.position.y = tH - 0.5 + Door.FRAME_THICKNESS;
            tfanLightGlass.position.y = tH - (this.mOpening.OpeningParams.fanlightHeight * 0.5) + Door.FRAME_THICKNESS;
            tfanLightGlass.position.z = -this.mOpening.depth * 0.5 + 0.0025;
            if (this.mOpening.mirrorHorizontal) {
                tfanLightGlass.position.z = -tfanLightGlass.position.z;
            }
            tfanLightGlass.material = this.getSheetGlassMaterial();
            if (this.mOpening.wall != null)
                tfanLightGlass.name = "room:wall|" + this.mOpening.wall.id.toString();
            this.prepare3dMesh(tfanLightGlass);
        }
        return (tfanLightGlass);
    }
    createFanLight(pParent) {
        let tW = this.mOpening.width;
        let tH = this.mOpening.height;
        let tfanLightFrame = CreateBox("room:opening:fanlightFrame", { height: Door.FRAME_THICKNESS,
            width: tW,
            depth: Door.FRAME_THICKNESS
        }, this.mScene);
        if (tfanLightFrame != null) {
            tfanLightFrame.parent = pParent;
            tfanLightFrame.position.y = tH - this.mOpening.OpeningParams.fanlightHeight + Door.FRAME_THICKNESS;
            tfanLightFrame.position.z = -this.mOpening.depth * 0.5 + 0.005;
            if (this.mOpening.mirrorHorizontal) {
                tfanLightFrame.position.z = -tfanLightFrame.position.z;
            }
            tfanLightFrame.material = this.mOpening.getMaterialofSet(this.mOpening.MaterialSet_Frame);
            if (this.mOpening.wall != null)
                tfanLightFrame.name = "room:wall|" + this.mOpening.wall.id.toString();
            this.prepare3dMesh(tfanLightFrame);
            this.createFanLight_glass(pParent);
        }
        return (tfanLightFrame);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _createHinge(pParent) {
        switch (this.mOpening.OpeningParams.doorType) {
            case WallOpening.DoorTypes.solid:
                this._createHinge_solid(pParent, this.mOpening.OpeningParams.doorTypeDouble);
                break;
            case WallOpening.DoorTypes.inlay:
                this._createHinge_inlay(pParent, this.mOpening.OpeningParams.doorTypeDouble);
                break;
            case WallOpening.DoorTypes.glass:
                this._createHinge_glass(pParent, this.mOpening.OpeningParams.doorTypeDouble);
                break;
            default:
                this._createHinge_solid(pParent, false);
        }
    }
    _createHinge_solid(pParent, pDouble) {
        if (!pDouble) {
            const trotPos = this.createHinge(pParent, Door.FRAME_OFFSET);
            const tLeaf = this.createLeaf(pParent, trotPos, Door.LEAF_DEPTH, false, true, Door.FRAME_THICKNESS);
            if (tLeaf != null) {
                this.createHandle(tLeaf, Door.FRAME_THICKNESS, Door.LEAF_DEPTH, false, undefined, this.mOpening.OpeningParams.doorType);
            }
        }
        else {
            const trotPos = this.createHinge(pParent, Door.FRAME_OFFSET);
            const trotPosR = this.createHinge(pParent, Door.FRAME_OFFSET, false);
            const tLeaf_L = this.createLeaf(pParent, trotPos, Door.LEAF_DEPTH, true, true, Door.FRAME_THICKNESS);
            if (tLeaf_L != null) {
                this.createHandle(tLeaf_L, Door.FRAME_THICKNESS, Door.LEAF_DEPTH, true, true, this.mOpening.OpeningParams.doorType);
            }
            const tLeaf_R = this.createLeaf(pParent, trotPosR, Door.LEAF_DEPTH, true, false, Door.FRAME_THICKNESS);
            if (tLeaf_R != null) {
                this.createHandle(tLeaf_R, Door.FRAME_THICKNESS, Door.LEAF_DEPTH, true, false, this.mOpening.OpeningParams.doorType);
            }
        }
    }
    _createHinge_inlay(pParent, pDouble) {
        if (!pDouble) {
            const trotPos = this.createHinge(pParent, Door.FRAME_OFFSET);
            const tLeaf = this.createLeaf_GlassInlay(pParent, trotPos, Door.LEAF_DEPTH, false, true, Door.FRAME_THICKNESS);
            if (tLeaf != null) {
                this.createHandle(tLeaf, Door.FRAME_THICKNESS, Door.LEAF_DEPTH, false, undefined, this.mOpening.OpeningParams.doorType);
            }
        }
        else {
            const trotPosL = this.createHinge(pParent, Door.FRAME_OFFSET);
            const trotPosR = this.createHinge(pParent, Door.FRAME_OFFSET, false);
            let tLeafL = this.createLeaf_GlassInlay(pParent, trotPosL, Door.LEAF_DEPTH, true, true, Door.FRAME_THICKNESS);
            if (tLeafL != null)
                this.createHandle(tLeafL, Door.FRAME_THICKNESS, Door.LEAF_DEPTH, true, true, this.mOpening.OpeningParams.doorType);
            let tLeafR = this.createLeaf_GlassInlay(pParent, trotPosR, Door.LEAF_DEPTH, true, false, Door.FRAME_THICKNESS);
            if (tLeafR != null)
                this.createHandle(tLeafR, Door.FRAME_THICKNESS, Door.LEAF_DEPTH, true, false, this.mOpening.OpeningParams.doorType);
        }
    }
    _createHinge_glass(pParent, pDouble) {
        if (!pDouble) {
            const trotPos = this.createHinge(pParent, Door.FRAME_OFFSET, true, Door.FRAME_THICKNESS, Door.GLASS_LEAF_THICKNESS);
            const tLeaf = this.createLeaf_Glass(pParent, trotPos, Door.GLASS_LEAF_THICKNESS, false, true, Door.FRAME_THICKNESS);
            if (tLeaf != null)
                this.createHandle_Glass(tLeaf, Door.FRAME_THICKNESS, false);
        }
        else {
            const trotPosL = this.createHinge(pParent, Door.FRAME_OFFSET, true, Door.FRAME_THICKNESS, Door.GLASS_LEAF_THICKNESS);
            const tLeafL = this.createLeaf_Glass(pParent, trotPosL, Door.GLASS_LEAF_THICKNESS, true, true, Door.FRAME_THICKNESS);
            if (tLeafL != null)
                this.createHandle_Glass(tLeafL, Door.FRAME_THICKNESS, true, true);
            const trotPosR = this.createHinge(pParent, Door.FRAME_OFFSET, false, Door.FRAME_THICKNESS, Door.GLASS_LEAF_THICKNESS);
            const tLeafR = this.createLeaf_Glass(pParent, trotPosR, Door.GLASS_LEAF_THICKNESS, true, false, Door.FRAME_THICKNESS);
            if (tLeafR != null)
                this.createHandle_Glass(tLeafR, Door.FRAME_THICKNESS, true, false);
        }
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Door.FRAME_DEPTH_OFFSET = 0.015;
Door.FRAME_OFFSET = 0.03;
Door.FRAME_THICKNESS = 0.04;
Door.LEAF_DEPTH = 0.04;
Door.GLASS_LEAF_THICKNESS = 0.01;
